import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { formGroup, formControl, FormDataType, useFormState, useFormConfig } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { UploadDrillBlockSettings } from './UploadDrillBlockSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { FileService, HTMLService } from '@business/services';
import { Container } from 'reactstrap';
import { Button } from '@core/components/Button';
import { UploadDrillBlockBloc } from './UploadDrillBlockBloc';
import { useBloc } from '@core/utils/bloc';
import { tap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { FormHTMLInline, FormInput } from '@core/components/form';
import './index.scss';
import { showYesNo } from '@modules/common';
import { FormUploadMultiple } from '@core/components/form/FormUploadMultiple';
import { UploadResponse } from '@business/entities/common';
import { R } from '@core/utils/r';
import { truncate } from 'lodash';

export enum UploadDrillBlockVariation {
  uploadDrill = 'uploadDrill',
}

function defineUploadDrillBlockForm(editable = false) {
  return formGroup({
    requireReview: formControl<boolean>({
      placeholder: 'Require Review',
      label: 'Require Review',
      initialValue: false,
      required: false,
    }),
    isSubmitted: formControl<boolean>({
      initialValue: false,
    }),
    files: formControl<UploadResponse[]>({ label: 'Attach Files', required: false }),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type UploadDrillForm = ReturnType<typeof defineUploadDrillBlockForm>;
export type UploadDrillFormData = FormDataType<typeof defineUploadDrillBlockForm>;

export const UploadDrillBlock = memo((props: CourseBuilder.BlockProps<UploadDrillBlockVariation>) => {
  const { variation, preview, lessonId, editable, step } = props;
  const form = useMemo(() => defineUploadDrillBlockForm(editable), [editable]);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(UploadDrillBlockBloc, lessonId);

  useConfigureBlock({
    props,
    form,
    contentComponent: UploadDrillBlockSettings,
    contentProps: { form, variation },
  });

  // id requiredReview tru then make required files
  useEffect(() => {
    form.controls.files.setConfig({
      required: formValue.requireReview,
    });
  }, [form, formValue.requireReview]);

  const removeFile = useCallback(
    (index: number) => {
      return of(null)
        .pipe(
          switchMap(() => showYesNo('Remove Image', 'Are you sure you want to remove this image?')),
          tap(res => {
            if (res) {
              const filteredFiles = formValue.files.filter((_, i) => i !== index);
              form.patchValue({ files: filteredFiles });
            }
          }),
        )
        .subscribe();
    },
    [form, formValue.files],
  );

  const onSubmit = useCallback(() => {
    if (props.editable || props.preview || R.isEmpty(lessonId)) {
      form.patchValue({ isSubmitted: true });
      return of(null);
    }

    if (formValue.requireReview && formValue.files.length == 0) {
      return of(null);
    }
    return bloc
      .submitUploadDrill(lessonId || '', {
        ...formValue,
        stepId: step?.id,
        blockId: props.id,
      })
      .pipe(
        switchMap(() => of(true)),
        tap(() => {
          form.patchValue({ isSubmitted: true });
        }),
      );
  }, [bloc, form, formValue, lessonId, props.editable, props.id, props.preview, step]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  useCourseForm(props.id, form, { onSubmit });

  return (
    <Container>
      <BlockContainer advanceForm={formValue.advance}>
        {/* <Card className="mt-4">
          <CardBody> */}
        {/* <FormInput rows={5} control={form.controls.essay} type="textarea" disabled={editable || formValue.isSubmitted} /> */}
        {!formValue.isSubmitted && (
          <FormUploadMultiple
            disabled={!(!editable && !formValue.isSubmitted)}
            type={`multiple`}
            viewType={'drop-zone'}
            control={form.controls.files}
            autoUpload
            autoUploadKey={'course-upload'}
          />
        )}
        <div className="dropzone-previews mb-2" id="file-previews" style={{ fontSize: 'x-small' }}>
          {formValue.files &&
            formValue.files.map((file, index) => {
              return (
                <MediaFileLineItem key={index} isRemoveable={!editable && !formValue.isSubmitted} file={file} removeFile={() => removeFile(index)} />
              );
            })}
        </div>

        <div className="d-flex">
          {!editable && !formValue.isSubmitted && (
            <div className="ml-2">
              <Button loading={submitting} className="mt-3 ml-2" color="primary" onClick={() => form.submit()}>
                Submit
              </Button>
            </div>
          )}
        </div>

        {/* </CardBody>
        </Card> */}
      </BlockContainer>
    </Container>
  );
});

export interface MediaFileLineItemProps {
  file: UploadResponse;
  removeFile?(): void;
  isRemoveable?: boolean;
  canPreview?: boolean;
}

export const MediaFileLineItem = memo((props: MediaFileLineItemProps) => {
  const { file, isRemoveable = false, canPreview = false } = props;

  const onView = useCallback(() => {
    window.open(file.fileUrl, '_blank');
  }, [file.fileUrl]);

  return (
    <div className="mt-3" key={'file'}>
      <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
        <div className="p-2">
          <Row className="align-items-center">
            <Col className="col-auto">
              {FileService.isImage(file.fileName) ? (
                <img data-dz-thumbnail="" height="80" className="avatar-sm rounded bg-light" alt={file.fileName} src={file.fileUrl} />
              ) : (
                <i className={`${FileService.getIcon(file.fileName)} font-size-24 `}></i>
              )}
            </Col>
            <Col className="">
              <div style={{ alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row', display: 'flex' }}>
                <div>
                  <a className="text-muted font-weight-bold">{file.fileName}</a>
                </div>
                {!!isRemoveable && (
                  <div>
                    <i
                      onClick={() => props.removeFile && props.removeFile()}
                      className="mdi mdi-close-circle-outline icon-lg font-size-22"
                      id="Imagetooltip"
                    ></i>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
});
